import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Mic, Users, BookOpen } from 'lucide-react';
import { aboutData, images } from '../data/mock';

const AboutPage = () => {
  return (
    <main>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#0d0d0d]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="caption text-[#c9a86c] mb-4 block opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>About</span>
              <h1 className="heading-1 text-[#f5f5f0] mb-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
                {aboutData.headline}
              </h1>
              <div className="gold-accent mb-8 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}></div>
            </div>
            
            <div className="relative opacity-0 animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={images.hero}
                  alt="Tyrre Burks"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border border-[#c9a86c]/30"></div>
              <div className="absolute -top-4 -right-4 w-32 h-32 border border-[#c9a86c]/20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="section-padding bg-[#1a1a18]">
        <div className="container-custom">
          <div className="max-w-3xl">
            <div className="space-y-6">
              {aboutData.bio.split('\n\n').map((paragraph, index) => (
                <p key={index} className="body-large text-[#a8a8a0]">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#0d0d0d] border-t border-b border-[rgba(168,168,160,0.1)]">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {aboutData.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-5xl md:text-6xl font-display font-bold text-[#c9a86c] mb-2">
                  {stat.value}
                </p>
                <p className="caption text-[#a8a8a0]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-padding bg-[#1a1a18]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-square overflow-hidden">
                <img
                  src={images.about}
                  alt="Tyrre Burks"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div>
              <span className="caption text-[#c9a86c] mb-4 block">Philosophy</span>
              <h2 className="heading-2 text-[#f5f5f0] mb-6">What I Believe</h2>
              <div className="gold-accent mb-8"></div>
              
              <div className="space-y-6">
                <div className="border-l-2 border-[#c9a86c] pl-6">
                  <p className="text-2xl font-display italic text-[#f5f5f0]">
                    "{aboutData.philosophy}"
                  </p>
                </div>
                
                <p className="body-large text-[#a8a8a0]">
                  Too many people spend their lives searching for purpose when the truth is: purpose is built, not found. It emerges when you integrate who you are with what you do.
                </p>
                
                <p className="body-large text-[#a8a8a0]">
                  I've dedicated my career to helping leaders see this clearly—not through hype or motivation, but through grounded, practical frameworks that actually work.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Drives Me */}
      <section className="section-padding bg-[#0d0d0d]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-[#c9a86c] mb-4 block">The Work</span>
            <h2 className="heading-2 text-[#f5f5f0] mb-6">What Drives Me</h2>
            <div className="gold-accent mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Mic, title: 'Speaking', desc: 'Delivering keynotes that transform, not just inspire.' },
              { icon: Users, title: 'Leadership', desc: 'Equipping leaders with frameworks for alignment.' },
              { icon: BookOpen, title: 'Teaching', desc: 'Making complex ideas accessible and actionable.' },
              { icon: Award, title: 'Integration', desc: 'Helping people tie their mission to daily life.' },
            ].map((item, index) => (
              <div
                key={item.title}
                className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-8 text-center card-hover group"
              >
                <div className="w-14 h-14 flex items-center justify-center border border-[#c9a86c]/30 text-[#c9a86c] mx-auto mb-6 group-hover:bg-[#c9a86c] group-hover:text-[#0d0d0d] transition-all">
                  <item.icon size={24} />
                </div>
                <h3 className="heading-3 text-[#f5f5f0] mb-3">{item.title}</h3>
                <p className="body-medium text-[#a8a8a0]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#1a1a18] border-t border-[rgba(168,168,160,0.1)]">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#0d0d0d] border border-[rgba(168,168,160,0.1)] p-10 text-center">
              <h3 className="heading-3 text-[#f5f5f0] mb-4">Book Tyrre to Speak</h3>
              <p className="body-medium text-[#a8a8a0] mb-6">
                Bring clarity and integration to your next event.
              </p>
              <Link to="/book" className="btn-primary inline-flex items-center gap-2">
                Request Availability
                <ArrowRight size={18} />
              </Link>
            </div>
            
            <div className="bg-[#0d0d0d] border border-[rgba(168,168,160,0.1)] p-10 text-center">
              <h3 className="heading-3 text-[#f5f5f0] mb-4">Discover Your Mission</h3>
              <p className="body-medium text-[#a8a8a0] mb-6">
                Let Ty help you discover your mission. Your life will never be the same when you live on mission.
              </p>
              <Link to="/ty-the-mission" className="btn-secondary inline-flex items-center gap-2">
                Start Discovering
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;